import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown in the fallback so operators can tell where it broke. */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions in the subtree so one broken page can't
 * unmount the whole app (which would take the shell's header + footer with
 * it, leaving a blank screen). Placed inside AppShell around the routed page
 * so the chrome survives, and once at the top level for catastrophic errors.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', this.props.label ?? '', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="grid min-h-[40vh] place-items-center p-8">
          <div className="max-w-md border border-coral/40 bg-abyss-2 p-6 text-center">
            <div className="font-display text-lg text-coral">Something broke on this page.</div>
            <p className="mt-2 font-mono text-xs text-ink-muted">
              {this.state.error.message || 'An unexpected error occurred.'}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 border border-hairline-strong px-4 py-1.5 font-mono text-label uppercase tracking-widest text-ink-muted hover:bg-abyss-3"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
